import { fetchEventSource } from '@tencent/fetch-event-source';
import { parseJson } from './data-utils';
import { reportErrorToTam, LogType } from '@src/slide/infrastructure/module/dependent/report/js-error/tam-report';
import { getXSRF } from '@src/slide/domain/slide-app/utils/cookie-utils';
import { i18n } from '../../edit-extension/edit-extension-export';

export interface GenerateSlideInput {
    outline: string;
    conversationId?: string;
    messageId?: string;
}

export enum ErrorCode {
    UNKNOWN = -1,
    /** 授权无效 */
    UNAUTHORIZED = 411203,
    /** 商业化相关错误 */
    COMMERCIALIZE_REASON = 11607,
    /** 敏感话题 */
    SENSITIVE_TOPICS = 411208,
    /** 模型繁忙 */
    MODEL_BUSY = 439005,
}

interface File {
    type: number;
    id: string;
    name: string;
    url: string;
    thumbnailUrl: string;
    iconUrl: string;
}

interface ContentData {
    content?: {
        status: 1 | 2;
        content?: {
            text?: string;
            files?: {
                items: Array<File>;
            };
        };
    };
}

interface ErrorFromServer1 {
    code: number;
    msg?: string;
    message?: string;
}

interface ErrorFromServer2 {
    error: ErrorFromServer1;
}

type ErrorFromServer = ErrorFromServer1 | ErrorFromServer2;
type Message = ContentData | ErrorFromServer;

export enum EventType {
    CONTENT = 'content',
    ERROR = 'error',
    DONE = 'done',
    STOP = 'stop',
}

type DataType<A, B> = {
    type: A;
    data: B;
};

interface ErrorMsg {
    code: number;
    message: string;
}

// 创建一个映射类型，将 EventType 的每个值映射到相应的数据类型
export type SSEData =
    | DataType<EventType.CONTENT, ContentData>
    | DataType<EventType.ERROR, ErrorMsg>
    | DataType<EventType.STOP, null>
    | DataType<EventType.DONE, null>;

const CONVERSATION_API = '/v1/ai/copilot/conversations/';
const SLIDE_GENERATION_API = '/api/ai/v1/copilot/streamactions';

enum EActionType {
    AI_FULL_REVIEW = 10005,
    AI_GENERATE = 10012,
    AI_SPEECH = 10016,
    GEN_PAGE = 10021,
}

enum PageType {
    /** 忠于原文 */
    original = 0,
    /** 适当扩写 */
    enhanced = 1,
}

const convertError = (error: Message): ErrorMsg => {
    if ('error' in error) {
        return {
            code: error.error.code,
            message: error.error.message ?? error.error.msg ?? '',
        };
    }

    if ('code' in error) {
        return {
            code: error.code,
            message: error.message ?? error.msg ?? '',
        };
    }

    return {
        code: ErrorCode.UNKNOWN,
        message: '',
    };
};

/** 流式请求 AI 服务生成 markdown */
export class SSEService {
    private static scenario = 'slide';
    private static abortController: AbortController | null = null;

    /** 大纲续写 slide 正文 */
    public static expandOutline(input: GenerateSlideInput, callback: (data: SSEData) => void) {
        const params = {
            action_type: EActionType.AI_GENERATE,
            expand_outline: {
                file_id: window.clientVars.padId,
                category: 'slide',
                message_id: input.messageId,
                outline: input.outline,
            },
        };
        const apiPath = `${window.location.origin}${SLIDE_GENERATION_API}?xsrf=${getXSRF()}`;

        return SSEService.request(apiPath, params, callback);
    }

    /** 单页 slide 生成 */
    public static generateSlidePage(
        input: { content: string; pageType?: PageType },
        callback: (data: SSEData) => void,
    ) {
        const params = {
            action_type: EActionType.GEN_PAGE,
            gen_slide_page: {
                page_type: input.pageType ?? PageType.original,
                content: input.content,
            },
        };
        const apiPath = `${window.location.origin}${SLIDE_GENERATION_API}?xsrf=${getXSRF()}`;

        return SSEService.request(apiPath, params, callback);
    }

    /** 生成演讲稿 */
    public static generateSpeech(content: string, callback: (data: SSEData) => void) {
        const params = {
            action_type: EActionType.AI_SPEECH,
            gen_slide_script: {
                content,
            },
        };

        const apiPath = `${window.location.origin}${SLIDE_GENERATION_API}?xsrf=${getXSRF()}`;

        return SSEService.request(apiPath, params, callback);
    }

    /** 全文审阅 */
    public static fullContentReview(content: string, callback: (data: SSEData) => void) {
        const params = {
            action_type: EActionType.AI_FULL_REVIEW,
            action_name: 'modify_content',
            modify_content: {
                modify_type: 10,
                text: content,
                input: '',
                title: '',
            },
        };

        const apiPath = `${window.location.origin}${SLIDE_GENERATION_API}?xsrf=${getXSRF()}`;

        return SSEService.request(apiPath, params, callback);
    }

    /** 备注转 doc */
    public static async createFile(noteContent: string): Promise<{
        success: true,
        fileUrl: string,
    } | {
        success: false,
        code: number;
        message: string;
    }> {
        const params = {
            content: {},
            files: [],
            action: {
                type: 26,
                createFile: {
                    category: 'doc',
                    fileText: noteContent,
                    textType: 1,
                },
            },
            historyDisplay: false,
            ext: {},
            resource: {},
            author: {
                role: 'user',
            },
        };
        const apiPath = `${window.location.origin}${CONVERSATION_API}?xsrf=${getXSRF()}`;
        return new Promise((resolve) => {
            let done = false;
            SSEService.request(apiPath, params, ({ data, type }) => {
                if (done) return;

                if (type === EventType.ERROR) {
                    resolve({ success: false, ...convertError(data) });
                    done = true;
                    return;
                }

                if (type === EventType.CONTENT) {
                    const fileUrl = data.content?.content?.files?.items[0].url;
                    if (fileUrl) {
                        resolve({ success: true, fileUrl });
                        done = true;
                    }
                    return;
                }

                if (type === EventType.DONE) {
                    if (!done) {
                        resolve({ success: false, code: ErrorCode.UNKNOWN, message: i18n.t('生成失败') });
                        done = true;
                    }
                    return;
                }
            });
        });
    }

    public static stop() {
        if (this.abortController) {
            this.abortController.abort();
            this.close();
        }
    }

    public static request(apiPath: string, params: any, callback: (data: SSEData) => void): { abort: () => void } {
        const abortController = new AbortController();
        let traceId = '';
        let isError = false;

        fetchEventSource(apiPath, {
            headers: {
                'X-AI-Scenario': this.scenario,
            },
            method: 'POST',
            body: JSON.stringify(params),
            signal: abortController.signal,
            openWhenHidden: true,
            fetch: SSEService.getFetchObject(),
            onopen: async (response: Response) => {
                traceId = response.headers?.get?.('X-Trace-Id') ?? '';
                if (response.status !== 200) {
                    isError = true;
                    return Promise.reject();
                }

                const contentType = response.headers.get('content-type');
                // 有些错误不会走到 sse 服务, 需要拦截处理
                if (contentType === 'application/json') {
                    isError = true;
                    // 错误处理
                    try {
                        const errorMsg = convertError(await response.clone().json());

                        // 用户未授权
                        if (errorMsg.code === ErrorCode.UNAUTHORIZED) {
                            import('@tencent/ai-sdk-common').then(({ showAiAuthModal }) => {
                                showAiAuthModal({
                                    onClose: () => {
                                        callback({ type: EventType.ERROR, data: errorMsg });
                                    },
                                    onConsent: () => {
                                        this.request(apiPath, params, callback);
                                    },
                                });
                            });
                            return;
                        }

                        callback({ type: EventType.ERROR, data: errorMsg });
                    } catch (error) {
                        return Promise.reject();
                    }
                }
                return Promise.resolve();
            },
            onmessage: ({ event, data }) => {
                if (!data) return;
                const message = parseJson<Message>(data);
                if (!message) return;

                const eventHandlerMapping = {
                    [EventType.CONTENT]: () => {
                        if ('content' in message && message.content?.content) {
                            callback({ type: EventType.CONTENT, data: message });
                        }
                    },
                    [EventType.ERROR]: () => {
                        const error = convertError(message);
                        isError = true;

                        // 用户未授权
                        if (error.code === ErrorCode.UNAUTHORIZED) {
                            import('@tencent/ai-sdk-common').then(({ showAiAuthModal }) => {
                                showAiAuthModal({
                                    onClose: () => {
                                        callback({ type: EventType.ERROR, data: error });
                                    },
                                    onConsent: () => {
                                        this.request(apiPath, params, callback);
                                    },
                                });
                            });
                            return;
                        }

                        callback({ type: EventType.ERROR, data: error });
                        return;
                    },
                };
                return eventHandlerMapping[event]?.();
            },
            onerror: (err) => {
                throw err;
            },
            onclose: () => {
                if (!isError) {
                    callback({ type: EventType.DONE, data: null });
                }
            },
        }).catch((error) => {
            isError = true;

            // eslint-disable-next-line no-param-reassign
            error.stack = `[AI] ${error.message} / ${error.stack}`;
            window.log.error(error, traceId);
            reportErrorToTam({ message: error.message, error }, LogType.ERROR);
            callback({ type: EventType.ERROR, data: { code: ErrorCode.UNKNOWN, message: '' } });
        });

        abortController.signal.addEventListener('abort', () => {
            if (!isError) {
                callback({ type: EventType.STOP, data: null });
            }
        });

        return {
            abort: () => abortController.abort(),
        };
    }

    private static close() {
        this.abortController = null;
    }

    private static getFetchObject = () => {
        // 由于 @tencent/tencent_doc_net 劫持了 fetch 库并且没有处理流式数据, 这里需要使用原始 fetch 对象
        const originFetch = (window as any).AI_SPACE?.fetch;
        if (originFetch) {
            return originFetch;
        }
        return window.fetch;
    };
}
